// External Imports
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner, styled, Text } from '@lessonnine/design-system.lib';

// Internal Imports
import {
  addAutoplayToUrl,
  addIframeAllowAutoplay,
  extractYoutubeVideoID,
  generateThumbnailImage,
  isYoutubeEmbedLink,
} from '../../utils/youtube.js';
import { stylesMap } from '../../utils/youtube.styles.js';

// Local Variables
const height = '360'; // 16:9 aspect ratio
const width = '640'; // 16:9 aspect ratio

const Content16by9AspectRatio = styled.div`
  align-items: center;
  aspect-ratio: 16 / 9;
  display: flex;
  height: 100%;
  justify-content: center;
  object-fit: contain;
  width: 100%;
  ${({ isLoading }) => (isLoading ? 'display: none;' : '')}
`;

const YoutubeHoverOverlay = styled.div`
  ${stylesMap.hoverOverlay}
`;

const YoutubeThumbnail = styled.img`
  ${stylesMap.thumbnail}
`;

const YoutubePlayButton = styled.button`
  ${stylesMap.playButton}
`;

const YoutubeVisuallyHidden = styled.span`
  ${stylesMap.visuallyHidden}
`;

// Component Definition
function FeaturedVideo({ title, url, onVideoClicked }) {
  const isYoutube = isYoutubeEmbedLink(url);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // We only know how to lazy load youtube embeds for now
  const [shouldLoad, setShouldLoad] = useState(!isYoutube);

  if (!shouldLoad && isYoutube) {
    const thumbnail = generateThumbnailImage(extractYoutubeVideoID(url));
    // Given we have a button lower below, the onClick for accessibility should bubble from there instead
    return (
      <Content16by9AspectRatio>
        {/* TODO: keyboard navigation is being ignored for now */}
        <YoutubeHoverOverlay
          onClick={() => {
            onVideoClicked({ url });
            setShouldLoad(true);
          }}
        >
          <YoutubeThumbnail alt="video thumbnail" src={thumbnail} />
          <YoutubePlayButton type="button">
            <YoutubeVisuallyHidden>Play</YoutubeVisuallyHidden>
          </YoutubePlayButton>
        </YoutubeHoverOverlay>
      </Content16by9AspectRatio>
    );
  }

  return (
    <>
      <Content16by9AspectRatio
        as="iframe"
        allow={addIframeAllowAutoplay('')}
        isLoading={isLoading}
        onError={() => setIsError(true)}
        onLoad={() => setIsLoading(false)}
        src={isYoutube ? addAutoplayToUrl(url) : url}
        title={title}
        height={height}
        width={width}
      />
      {isLoading ||
        (isError && (
          <Content16by9AspectRatio>
            {isLoading && <Spinner size={64} />}
            {/* TODO: we should have translations for errors */}
            {isError && <Text>An unexpected error occured</Text>}
          </Content16by9AspectRatio>
        ))}
    </>
  );
}

FeaturedVideo.propTypes = {
  onVideoClicked: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
};

// Module Exports
export { FeaturedVideo };
